import {AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, Injectable, Injector, input, output, runInInjectionContext, signal, viewChild, viewChildren} from "@angular/core";
import {catchError, map} from 'rxjs/operators';
import {EnkelGruppeModell, GruppeId, ModelloppdateringType, MøteId, MøteMedAggregaterModell, MøteModellEnkel, MøteRessursClient, OmkringliggendeMøterModell, OppdateringmodellMøteOgSak, OppdateringmodellSakFlyttetTilMøte, PlanId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {concatMap, debounceTime, distinctUntilChanged, filter, from, fromEvent, merge, mergeMap, MonoTypeOperatorFunction, Observable, of, Subject, switchMap, takeUntil, tap} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {erMøteAvholdt, lesMøtedato2, sorterMøterPåDato} from "felles-ng2/modell/møteModellHjelpere";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erTomListe, første, siste} from "felles-ng2/støttefunksjoner/lister";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {Temporal} from "temporal-polyfill";
import {elementSynlighetObserveringSomProvider} from "felles-ng2/tjenester/ElementSynlighetObservering";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {HorisontalMøtenavigasjonInnslagKomponent, HorisontalMøtenavigasjonInnslagKomponent_ElementSynlighetObservering_DI_TOKEN, Innslag, InnslagType, Møteinfo} from "../horisontal-møtenavigasjon-innslag/horisontal-møtenavigasjon-innslag";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {distinctUntilArrayItemChanged, filterNil} from "@ngneat/elf";
import {GjeldendeLokale} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, I18nTekst} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {TranslateModule} from "@ngx-translate/core";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MøteMedAggregaterDatatjeneste} from "felles-ng2/tjenester/datalager/MøteMedAggregaterDatatjeneste";
import {modell, møteIdFraForeldre} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {MøteOpprettDialogKomponent} from "web-ng2/moduler/møte/modale/møte-opprett-dialog/møte-opprett-dialog";
import {MatDialog} from "@angular/material/dialog";
import {fraUiHendelser} from "felles-ng2/infrastruktur/rxjs/observerbarHjelpere";
import {promiseMedResolvere} from "felles-ng2/støttefunksjoner/promiser";
import {erTouchEvent} from "felles-ng2/støttefunksjoner/dom";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import ZonedDateTime = Temporal.ZonedDateTime;
import {PresentasjonsmodusTjeneste} from "web-ng2/tjenester/PresentasjonsmodusTjeneste";
import {leverForsinketMedMindreAnnenHendelse} from "felles-ng2/støttefunksjoner/observable";

const debug = initDebugger('HorisontalMøtenavigasjonKomponent');

/**
 * Horisontal listing av møter med støtte for lasting av flere møter når bruker skroller til endene
 * @todo relaste utvalg ved møte slettet / nytt / flyttet pga møtenummer?
 */
@Component({
    selector: 'horisontal-moetenavigasjon',
    templateUrl: 'horisontal-møtenavigasjon.html',
    styleUrl: 'horisontal-møtenavigasjon.scss',
    host: {
        'class': 'ikke-print',
        '[class.smal-visning]': 'smalVisning()', //aktiverer 'smal' visning når presentasjonsmodus er aktivert
        'data-testid': 'horisontal-møtenavigasjon'
    },
    imports: [MatIcon, MatIconButton, HorisontalMøtenavigasjonInnslagKomponent, TipsKomponent, TranslateModule],
    //angir rootMargin slik at harBlittSynlig blir kalt N piksler før elementet blir synlig i kontainer
    providers: [elementSynlighetObserveringSomProvider(HorisontalMøtenavigasjonInnslagKomponent_ElementSynlighetObservering_DI_TOKEN, el => ({root: el.querySelector('.skrollkontainer'), rootMargin: '3px'}))],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorisontalMøtenavigasjonKomponent implements AfterViewInit {
    static ajsSelector = 'horisontalMoetenavigasjon';
    static lastTidligereSenereDebounce = 1000;

    kontainer = viewChild.required<ElementRef<HTMLElement>>('kontainer')
    skrollkontainer = viewChild.required<ElementRef<HTMLElement>>('skrollkontainer')

    vedKlikkPaaNyttMoete = output();

    aktivtMoeteId = input<MøteId | null>(null);

    /** Liste med Innslag, manipuleres via Kommandoer som konsumeres i {@link #startKommandoloop} */
    innslagsamling = signal(InnslagSamling.tomSamling(this.opprettInnslagSamlingAvhengigheter()));
    innslag = computed(() => this.innslagsamling().innslag);
    ingenMoeter = computed(() => this.innslagsamling().inneholderIngenMøter());
    initialisert = signal(false);
    lasterData = asynkArbeidPågårSignal();
    draOgSlippPaagaar = signal(false);
    undertrykkKlikkPaaMoete = signal(false);
    /** Aktiverer smal visning dersom bruker har aktivert presentasjonsmodus */
    smalVisning = computed(() => this.presentasjonsmodusTjeneste.erAktivert());
    /** Aktiverer ekspandert smal visning når bruker flytter mus inn i horisontal-møtenavigasjon ved smal visning */
    smalVisningEkspandert = this.smalVisningEkspandertVedHover();

    public readonly innslagBredde = 200;

    //public for testbarhet
    public lastTidligereMøterTrigger = new Subject<void>()
    //public for testbarhet
    public lastSenereMøterTrigger = new Subject<void>()
    private kommandoAvhengigheter = this.opprettKommandoAvhengigheter();

    constructor(private avhengigheter: HorisontalMøtenavigasjonKomponentAvhengigheter,
                private presentasjonsmodusTjeneste: PresentasjonsmodusTjeneste,
                private kontekst: BrukerkontekstTilbyder,
                private klokke: Klokke,
                private gjeldendeLokale: GjeldendeLokale,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private navigasjon: Navigasjon,
                private modelloppdateringer: Modelloppdateringer,
                private matDialog: MatDialog,
                private injector: Injector) {

    }

    vedInnslagSynlig(innslag: Innslag) {
        if (innslag.type() == InnslagType.stedfortrederFoer)
            this.lastTidligereMøterTrigger.next();
        if (innslag.type() == InnslagType.stedfortrederEtter)
            this.lastSenereMøterTrigger.next();
    }

    vedKlikkPaaInnslag(innslag: Innslag, event: MouseEvent) {
        //vi vil ikke gå til møte når bruker har til hensikt å 'bla' i møtene vhja dra-og-slipp
        if (this.undertrykkKlikkPaaMoete() || innslag.type() != InnslagType.moete) return;
        this.navigasjon.gåTilMøte(innslag.moeteinfo()!.moete.id);
    }

    visTidligere() {
        const kontainer = this.skrollkontainer().nativeElement;
        kontainer.scrollLeft = kontainer.scrollLeft - kontainer.clientWidth
    }

    visSenere() {
        const kontainer = this.skrollkontainer().nativeElement;
        kontainer.scrollLeft = kontainer.scrollLeft + kontainer.clientWidth
    }

    gaaTilOpprettMoete() {
        MøteOpprettDialogKomponent.vis(this.matDialog);
    }

    // angular lifssyklus

    ngAfterViewInit(): void {
        //nå er skrollkontainer initialisert!
        runInInjectionContext(this.injector, () => {
            this.startKommandoloop()
            this.skrollMedDraOgSlipp();
        });
    }

    // private hjelpemetoder

    /** Laster data som følge av hendelser og oppdaterer {@link #innslag} */
    private startKommandoloop() {
        //visse hendelser skal ignoreres dersom vi har en aktivt datalasting eller vi ikke har initialisert
        //uten dette vil feks den initielle skrollingen føre til multiple uønskede Hendelse.lastTidligere hendelser
        const ignorerMensDatalasting = filter(() => this.initialisert() && !this.lasterData());

        const interneKommandoer = new Subject<Kommando>()
        merge(
          //kommandoer produsert av kall til Kommando.vedVis
          interneKommandoer,
          //start med å laste inneværende møter
          of(new VisInneværende(this.kommandoAvhengigheter)),
          //fokuser på forespurt møte (og sørg for at møtet er i innslag-listen vår)
          toObservable(this.aktivtMoeteId).pipe(filterNil(), map(id => new FokuserMøte(this.kommandoAvhengigheter, id))),
          //last flere møter når man skroller til endene
          this.lastTidligereMøterNårStedfortrederFørBlirSynlig(ignorerMensDatalasting),
          this.lastSenereMøterNårStedfortrederEtterBlirSynlig(ignorerMensDatalasting),
          //lytter til endring i språk da horisontal-møtenavigasjon er en komponent som lever svært lenge
          this.gjeldendeLokale.brukersLokaleSomStrøm().pipe(ignorerMensDatalasting, map(() => new SpråkEndret(this.kommandoAvhengigheter))),
          //lytt til endringer i vindusstørrelse og oppdater antall stedfortredere
          this.reberegnTilgjengeligBreddeVedEndringAvVindusstørrelse().pipe(ignorerMensDatalasting as MonoTypeOperatorFunction<Kommando>),
          // Oppdaterer møteinformasjon i viste innslag ved endringer i datalager for {@link MøteMedAggregaterModell
          this.oppdaterInnslagVedDatalagerOppdateringer().pipe(ignorerMensDatalasting as MonoTypeOperatorFunction<Kommando>),
          //lytt til eksterne hendelser (modelloppdateringer) som vi bør reagere på
          this.oppdaterInnslagVedEksterneModelloppdateringer2()
        ).pipe(
          tap(kommando => debug(`kommando ${kommando.constructor.name} mottatt`)),
          //behandler kommandoer sekvensielt, dvs behandler ikke neste kommando før observable produsert av forrige er ferdig
          concatMap(kommando => {
              const laster = this.lasterData.start();
              return kommando.muterInnslag(this.innslagsamling()).pipe(
                //signaliser at data er ferdig lastet - brukes i filter tidligere i pipen
                laster.avsluttVedLeveranse(),
                //ikke terminer kommando-pipen ved feil - vise feilmelding og fortsett med neste kommando
                catchError(error => {
                    console.error(error);
                    this.meldingTilBruker.feilmelding(new I18nTekst(i18n('Feil ved lasting av møter')));
                    return of(this.innslagsamling());
                }),
                map(innslag => ({innslag, kommando: kommando}))
              );
          }),
          takeUntilDestroyed()
        )
          .subscribe(async ({innslag, kommando}) => {
                this.innslagsamling.set(innslag);
                const nyeKommandoer = await kommando.vedVis(this.innslagsamling(), this.skrollkontainer().nativeElement);
                this.initialisert.set(true);
                debug(`kommando ${kommando.constructor.name} leverte ${innslag.toString()}`, innslag)
                nyeKommandoer.forEach(kommando => interneKommandoer.next(kommando));
            }
          )
    }

    private lastTidligereMøterNårStedfortrederFørBlirSynlig(ignorerMensDatalasting: MonoTypeOperatorFunction<unknown>): Observable<LastTidligere> {
        return this.lastTidligereMøterTrigger.pipe(
          ignorerMensDatalasting,
          //uten en debounce vil nye stedfortredere bli lagt til så fort at den pågående smooth-scrollen potensielt scroller inn i disse stedfortrederne som så fører til lasting av flere møter osv.
          debounceTime(HorisontalMøtenavigasjonKomponent.lastTidligereSenereDebounce),
          map(() => new LastTidligere(this.kommandoAvhengigheter)));
    }

    private lastSenereMøterNårStedfortrederEtterBlirSynlig(ignorerMensDatalasting: MonoTypeOperatorFunction<unknown>): Observable<LastSenere> {
        return this.lastSenereMøterTrigger.pipe(
          ignorerMensDatalasting,
          //uten en debounce vil nye stedfortredere bli lagt til så fort at den pågående smooth-scrollen potensielt scroller inn i disse stedfortrederne som så fører til lasting av flere møter osv.
          debounceTime(HorisontalMøtenavigasjonKomponent.lastTidligereSenereDebounce),
          map(() => new LastSenere(this.kommandoAvhengigheter)));
    }

    /** Oppdaterer møteinformasjon i viste innslag ved endringer i datalager for {@link MøteMedAggregaterModell} */
    private oppdaterInnslagVedDatalagerOppdateringer(): Observable<ErstattMøter> {
        return toObservable(this.innslagsamling).pipe(
          map(innslag => innslag.møteIder()),
          distinctUntilArrayItemChanged<MøteId>(),
          switchMap(møteIder => this.avhengigheter.møteendringer(møteIder)),
          map(oppdaterteMøter => new ErstattMøter(this.kommandoAvhengigheter, oppdaterteMøter)));
    }

    /**
     * Oppdaterer datalager for {@link MøteMedAggregaterModell} ved relevante eksterne modelloppdateringer.
     * Disse oppdateringene propagerer til {@link #innslag} via {@link #oppdaterInnslagVedDatalagerOppdateringer}.
     *
     * Vi trenger å reagere på følgende endringer knyttet til møter i innslag for {@link #gjeldendePeriode}
     * - MøteMedAggregaterModell - lokale endringer propagerer til {@link MøteMedAggregaterDatatjeneste}.
     * - endringer i sak (pga sak ansvar info)
     * - endringer i oppgave (pga oppgave lest teller)
     * - endringer i dokument (pga dokument lest teller)
     */
    private oppdaterInnslagVedEksterneModelloppdateringer2(): Observable<Kommando> {
        return this.modelloppdateringer.hendelser
          .pipe(
            mergeMap(async hendelse => {
                switch (hendelse.type) {
                  //pga potensiell endring i møtenummerformat
                    case ModelloppdateringType.PLAN_OPPDATERT:
                        return this.innslagsamling().inneholderInnslagKnyttetTilPlan(modell<PlanId>(hendelse)) ?
                          [new RelastPeriode(this.kommandoAvhengigheter)] : [];

                  //møte oppdatert/slett håndteres via MøteMedAggregaterDatatjeneste.lyttTilModelloppdateringer
                    case ModelloppdateringType.NYTT_MØTE:
                        const nyttMøteDato = await this.avhengigheter.møtedato(modell<MøteId>(hendelse));
                        return nyttMøteDato.overlapper(this.innslagsamling().gjeldendePeriode()) ?
                          [new RelastPeriode(this.kommandoAvhengigheter)] : [];

                  //pga saksansvar-informasjon
                    case ModelloppdateringType.NY_SAK:
                    case ModelloppdateringType.SAK_OPPDATERT:
                        return RelastMøte.for([møteIdFraForeldre(hendelse)], this.kommandoAvhengigheter);

                    case ModelloppdateringType.SAK_FLYTTET_TIL_MØTE:
                        const hendelsemodell = modell<OppdateringmodellSakFlyttetTilMøte>(hendelse);
                        return RelastMøte.for([hendelsemodell.moeteId, hendelsemodell.opprinneligMoeteId], this.kommandoAvhengigheter);

                    case ModelloppdateringType.SAK_SLETTET:
                        return RelastMøte.for([modell<OppdateringmodellMøteOgSak>(hendelse).moeteId], this.kommandoAvhengigheter);

                  //pga oppgaveteller
                    case ModelloppdateringType.NY_OPPGAVE:
                    case ModelloppdateringType.OPPGAVE_OPPDATERT:
                    case ModelloppdateringType.OPPGAVE_SLETTET:
                        return RelastMøte.for([modell<OppdateringmodellMøteOgSak>(hendelse).moeteId], this.kommandoAvhengigheter);

                    default:
                        return [];
                }
            }),
            //flater ut lista
            concatMap(oppdateringsHendelser => from(oppdateringsHendelser))
          )
    }

    private reberegnTilgjengeligBreddeVedEndringAvVindusstørrelse(): Observable<ReberegnTilgjengeligBredde> {
        return fromEvent(window, 'resize')
          .pipe(
            debounceTime(1000),
            map(() => new ReberegnTilgjengeligBredde(this.kommandoAvhengigheter))
          )
    }

    private async forsinketSkrollTilInnslag(innslag: Innslag | null, plassering: ScrollLogicalPosition = 'center') {
        if (erNil(innslag)) return;
        //sørger for at skrollTilInnslag kalles i neste sykel, slik at endringer i innslag har blitt tegnet ut
        await new Promise(resolve => setTimeout(() => {
            this.skrollTilInnslag(innslag, plassering);
            resolve(undefined);
        }, 0));
        //vent litt til slik at animasjonen som 'scroller' har rukket å kjøre
        await new Promise(resolve => setTimeout(resolve, 1000));
    }

    private skrollTilInnslag(innslag: Innslag, plassering: ScrollLogicalPosition = 'center') {
        debug(`skrollTilInnslag: innslag.id: ${innslag.toString()}`, innslag)
        this.domElementKnyttetTilInnslag(innslag).scrollIntoView({behavior: 'smooth', inline: plassering, block: 'end'});
    }

    private domElementKnyttetTilInnslag(innslag: Innslag): HTMLElement {
        return this.skrollkontainer().nativeElement.querySelector(`#${innslag.id}`)!;
    }

    /** Sørger for at man kan skrolle vhja dra-og-slipp.
     * I ajs-versjonen ble det benyttet et 'karusell'-bibliotek der man KUN kunne 'skrolle' vhja dra-og-slipp, viderefører
     * derfor støtte for dra-og-slipp her */
    private skrollMedDraOgSlipp() {
        const kontainer = this.skrollkontainer().nativeElement;

        function somUiHendelseMedPageX(hendelse: MouseEvent | TouchEvent): { hendelse: MouseEvent | TouchEvent, pageX: number } {
            return {
                hendelse: hendelse,
                pageX: erTouchEvent(hendelse) ? hendelse.touches[0].pageX : hendelse.pageX
            }
        }

        const musNed = fraUiHendelser<MouseEvent | TouchEvent>(kontainer, 'mousedown', 'touchstart').pipe(map(somUiHendelseMedPageX));
        const musFlyttet = fraUiHendelser<MouseEvent | TouchEvent>(kontainer, 'mousemove', 'touchmove').pipe(map(somUiHendelseMedPageX));
        const musOpp = fraUiHendelser<MouseEvent | TouchEvent>(kontainer, 'mouseup', 'touchend');
        const musForlattMøteliste = fromEvent<MouseEvent>(kontainer, 'mouseleave');

        musNed
          .pipe(
            map(musNed => ({startX: musNed.pageX, scrollLeft: kontainer.scrollLeft})),
            //lag en strøm so m leverer hvor mange piksler bruker har dratt i horisontal akse så lenge bruker 'drar'
            switchMap(startTilstand =>
              musFlyttet.pipe(
                //signaliser at en 'dra'-operasjon har startet (brukes til å modifisere css slik at man kan se at bruker har dratt)
                tap(musFlyttetHendelse => this.draOgSlippPaagaar.set(true)),
                //undertrykk klikk-oppførsel (dvs 'naviger til møte') når bruker har dratt mer enn 20 piksler (nødvendig da man ved klikk med mus fort vekk flytter på musa mens man klikker)
                tap(musFlyttetHendelse => Math.abs(musFlyttetHendelse.pageX - startTilstand.startX) > 20 ? this.undertrykkKlikkPaaMoete.set(true) : undefined),
                //beregn hvor langt bruker har dratt i horisontal retning
                map(musFlyttetHendelse => startTilstand.scrollLeft - (musFlyttetHendelse.pageX - startTilstand.startX)),
                //avslutt 'dra'-oppførsel når bruker slipper opp museknapp eller dra musen utenfor den horisontale møtelisten
                takeUntil(merge(
                  musOpp.pipe(
                    tap(() => this.draOgSlippPaagaar.set(false)),
                    tap(() => this.undertrykkKlikkPaaMoete.set(false))),
                  musForlattMøteliste))
              )
            ),
            takeUntilDestroyed()
          )
          //utfør skrolling
          .subscribe(scrollLeft => kontainer.scrollLeft = scrollLeft);
    }

    /** Beregner hvor mange hele innslag det er plass til gitt nåværende vindusbredde */
    //public for testbarhet
    public beregnAntallInnslag() {
        return Math.floor(this.skrollkontainer().nativeElement.clientWidth / this.innslagBredde);
    }

    /** Beregner indeks til første og siste innslag som er helt eller delvis synlig i skrollkontainer. */
    private beregnSynligUtsnitt(): { startPos: number, sluttPos: number } {
        const kontainer = this.skrollkontainer().nativeElement;
        const førsteSynligeInnslag = Math.floor(kontainer.scrollLeft / this.innslagBredde)
        const sisteSynligeInnslag = Math.floor((this.skrollkontainer().nativeElement.clientWidth + kontainer.scrollLeft) / this.innslagBredde)
        return {startPos: førsteSynligeInnslag, sluttPos: Math.min(sisteSynligeInnslag, this.innslagsamling().innslag.length - 1)}
    }

    private opprettKommandoAvhengigheter(): KommandoAvhengigheter {
        return {
            forsinketSkrollTilInnslag: (innslag, plassering) => this.forsinketSkrollTilInnslag(innslag, plassering),
            beregnSynligUtsnitt: () => this.beregnSynligUtsnitt(),
            beregnAntallInnslag: () => this.beregnAntallInnslag(),
            møterIPeriode: (periode) => this.avhengigheter.møterIPeriode(periode),
            hentMøte: (id) => this.avhengigheter.hentMøte(id),
            førstkommendeMøteOgOmkringliggende: (maksAntall) => this.avhengigheter.førstkommendeMøteOgOmkringliggende(maksAntall),
            møteOgOmkringliggende: (møteId, maksAntall) => this.avhengigheter.møteOgOmkringliggende(møteId, maksAntall),
            finnTidligereMøter: (før, maksAntall) => this.avhengigheter.finnTidligereMøter(før, maksAntall),
            finnSenereMøter: (etter, maksAntall) => this.avhengigheter.finnSenereMøter(etter, maksAntall)
        };
    }

    private opprettInnslagSamlingAvhengigheter(): InnslagSamlingAvhengigheter {
        return {
            klokke: this.klokke,
            gruppe: (id) => this.kontekst.autentisertBruker().finnAutentiserteGruppe(id)!,
            beregnAntallInnslag: () => this.beregnAntallInnslag()
        };
    }

    private smalVisningEkspandertVedHover() {
        return toSignal(toObservable(this.kontainer).pipe(
          map(lenke => lenke.nativeElement),
          switchMap(lenke =>
            merge(
              fromEvent(lenke, 'mouseenter')
                .pipe(
                  //ikke trigg visningEkspander=true dersom mus flyttes UT like etter at mus ble flyttet INN
                  leverForsinketMedMindreAnnenHendelse(500, lenke, 'mouseleave'),
                  map(() => true)),
              fromEvent(lenke, 'mouseleave')
                .pipe(
                  //ikke trigg visningEkspander=true dersom mus flyttes INN like etter at mus ble flyttet UT
                  leverForsinketMedMindreAnnenHendelse(200, lenke, 'mouseenter'),
                  map(() => false)))
          ),
          //returnerer alltid false dersom smal visning ikke er aktivert
          map(ekspandert => this.smalVisning() && ekspandert)
        ), {initialValue: false});
    }

}

/** Kommandoer brukes for å manipulere {@link InnslagSamling} (eksponert via {@link #innslagsamling}) */
abstract class Kommando {
    constructor(protected avhengigheter: KommandoAvhengigheter) {
    }

    /** Muterer innslag, normalt ved å returnere en kopi av innslag opprettet vhja en av innslag.med... metoden.
     * Ansvarlig for å laste data (møter) og legge til nødvendige stedfortredere */
    abstract muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling>;

    /** Kalles når innslag levert av muterInnslag har blitt satt i gui,
     * kan returnere nye kommandoer som legges til kommando-køen for utføring. */
    async vedVis(innslag: InnslagSamling, skrollkontainer: HTMLElement): Promise<Kommando[]> {
        return [];
    }

    protected get beregnAntallHeltEllerDelvisSynligeInnslag(): number {
        return this.avhengigheter.beregnAntallInnslag() + 2; //legger til ett i hver ende - pga mulig delvis synlig element
    }
}

class VisInneværende extends Kommando {

    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        const maksAntall = this.beregnAntallHeltEllerDelvisSynligeInnslag;
        return this.avhengigheter.førstkommendeMøteOgOmkringliggende(maksAntall)
          .pipe(map(møter => innslag.medKunMøtene(møter.moeter)
            .medStedfortredereFør(møter.antallTidligere < maksAntall)
            .medStedfortredereEtter(møter.antallSenere < maksAntall)
          ));
    }

    async vedVis(innslag: InnslagSamling, skrollkontainer: HTMLElement) {
        if (erIkkeNil(innslag.førsteIkkeAvholdteMøteinnslag()))
            await this.avhengigheter.forsinketSkrollTilInnslag(innslag.førsteIkkeAvholdteMøteinnslag()! as Innslag, 'center');
        else
            await this.avhengigheter.forsinketSkrollTilInnslag(innslag.sisteMøteinnslag()! as Innslag, 'end');
        return [];
    }
}

class FokuserMøte extends Kommando {

    constructor(avhengigheter: KommandoAvhengigheter, private møteId: MøteId) {
        super(avhengigheter);
    }

    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        //dersom aktiverMøte-hendelse og møtet allerede er lastet trenger vi ikke å laste data
        if (innslag.inneholderInnslagForMøteId(this.møteId)) return of(innslag);
        const maksAntall = this.beregnAntallHeltEllerDelvisSynligeInnslag;
        return this.avhengigheter.møteOgOmkringliggende(this.møteId, maksAntall)
          .pipe(map(møter => innslag.medKunMøtene(møter.moeter)
            .medStedfortredereFør(møter.antallTidligere < maksAntall)
            .medStedfortredereEtter(møter.antallSenere < maksAntall)
          ));
    }

    async vedVis(innslag: InnslagSamling, skrollkontainer: HTMLElement): Promise<Kommando[]> {
        const aktivt = innslag.innslagForMøteId(this.møteId)
        await this.avhengigheter.forsinketSkrollTilInnslag(aktivt, 'center');

        //last mer data dersom forespurt møte ligger i ytterkant av tilgjengelige innslag
        const synligUtsnitt = this.avhengigheter.beregnSynligUtsnitt();
        return [
            ...(innslag.på(synligUtsnitt.startPos).type() == InnslagType.stedfortrederFoer ? [new LastTidligere(this.avhengigheter, false)] : []),
            ...(innslag.på(synligUtsnitt.sluttPos).type() == InnslagType.stedfortrederEtter ? [new LastSenere(this.avhengigheter)] : []),
        ];
    }
}

class LastTidligere extends Kommando {

    constructor(avhengigheter: KommandoAvhengigheter, private scrollTil = true) {
        super(avhengigheter);
    }

    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        //henter tilstrekkelig mange innslag slik at alle potensielle stedfortredere som vises blir populert med møter
        const maksAntall = this.beregnAntallHeltEllerDelvisSynligeInnslag;
        return this.avhengigheter.finnTidligereMøter(innslag.gjeldendePeriode().start, maksAntall)
          .pipe(
            map(møter => innslag.medTidligereMøter(møter, møter.length < maksAntall))
          );
    }

    async vedVis(innslag: InnslagSamling, skrollkontainer: HTMLElement): Promise<Kommando[]> {
        //pga at safari ikke støtter overflow-anchor (se https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor)
        //må vi her simulere denne funksjonaliteten - som går ut på bruker ikke skal oppleve at innholdet scroller når ny
        //stedfortredere legges til i starten av innslag-samlingen.
        const initiellScrollWidth = skrollkontainer.scrollWidth
        const returPromise = promiseMedResolvere<Kommando[]>()
        //sørger for å utføre nødvendig scroll som gjør at inneholdet ikke oppleves som å ha flyttet seg
        //ETTER at nye stedfortredere har blitt lagt til og tegnet ut i GUI
        requestAnimationFrame(() => {
            //midlertidig deaktiver 'smooth scroll' - vi trenger at scrollingen skjer øyeblikkelig
            skrollkontainer.style.scrollBehavior = 'auto';
            skrollkontainer.style.scrollSnapType = 'none';
            skrollkontainer.scrollLeft = skrollkontainer.scrollLeft + (skrollkontainer.scrollWidth - initiellScrollWidth);

            //reaktiver 'smooth scroll'
            requestAnimationFrame(() => {
                skrollkontainer.style.scrollBehavior = 'smooth';
                skrollkontainer.style.scrollSnapType = 'x proximity';
                returPromise.resolve([]);
            });
        });
        return returPromise.promise;
    }
}

class LastSenere extends Kommando {

    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        const maksAntall = this.beregnAntallHeltEllerDelvisSynligeInnslag;
        return this.avhengigheter.finnSenereMøter(innslag.gjeldendePeriode().slutt, maksAntall)
          .pipe(
            map(møter => innslag.medSenereMøter(møter, møter.length < maksAntall))
          );
    }
}


class RelastPeriode extends Kommando {
    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        return this.avhengigheter.møterIPeriode(innslag.gjeldendePeriode())
          .pipe(map(møter => innslag.medKunMøtene(møter)));
    }
}

class SpråkEndret extends RelastPeriode {

}

class ReberegnTilgjengeligBredde extends Kommando {
    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        return of(innslag.medReberegnetUtsnitt());
    }
}

class RelastMøte extends Kommando {
    static for(møteIder: (MøteId | null)[], avhengigheter: KommandoAvhengigheter): RelastMøte[] {
        return møteIder.filter(erIkkeNil).map(id => new RelastMøte(avhengigheter, id));
    }

    constructor(avhengigheter: KommandoAvhengigheter, private møteId: MøteId) {
        super(avhengigheter);
    }

    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        return this.avhengigheter.hentMøte(this.møteId)
          .pipe(map(møte => innslag.medMøte(møte)));
    }
}

class ErstattMøter extends Kommando {
    constructor(avhengigheter: KommandoAvhengigheter, private møter: MøteMedAggregaterModell[]) {
        super(avhengigheter);
    }

    muterInnslag(innslag: InnslagSamling): Observable<InnslagSamling> {
        return of(innslag.medKunMøtene(this.møter));
    }
}

interface KommandoAvhengigheter {

    forsinketSkrollTilInnslag(innslag: Innslag | null, plassering: ScrollLogicalPosition): Promise<void>;

    /** Beregner indeks til første og siste innslag som er helt eller delvis synlig i skrollkontainer. */
    beregnSynligUtsnitt(): { startPos: number, sluttPos: number };

    /** Beregner hvor mange hele innslag det er plass til gitt nåværende vindusbredde */
    beregnAntallInnslag(): number;

    møterIPeriode(periode: ZonedDateTimePeriodeLukket): Observable<MøteMedAggregaterModell[]>;

    hentMøte(id: MøteId): Observable<MøteMedAggregaterModell>;

    førstkommendeMøteOgOmkringliggende(maksAntall: number): Observable<OmkringliggendeMøterModell>;

    møteOgOmkringliggende(møteId: MøteId, maksAntall: number): Observable<OmkringliggendeMøterModell>;

    finnTidligereMøter(før: ZonedDateTime, maksAntall: number): Observable<MøteMedAggregaterModell[]>;

    finnSenereMøter(etter: ZonedDateTime, maksAntall: number): Observable<MøteMedAggregaterModell[]>;
}

/** Samling med innslagene som vises i møtenavigasjonen.
 * Vi bruker med... metodene for å mutere listen med innslag og returnere ny InnslagSamling.
 * MERK at Innslag instanser i samlingen også kan mutere, se dokumentasjon på {@link Innslag} */
class InnslagSamling {

    readonly innslag: Innslag[] = [];

    private constructor(private avhengigheter: InnslagSamlingAvhengigheter, innslag: Innslag[]) {
        this.innslag = innslag;
    }

    static tomSamling(avhengigheter: InnslagSamlingAvhengigheter): InnslagSamling {
        return new InnslagSamling(avhengigheter, []);
    }

    /** Returnerer kopi av innslag der møteinnslag er erstattet med angitte møter, stedfortredere før og etter forblir uendret */
    medKunMøtene(møter: MøteMedAggregaterModell[]): InnslagSamling {
        const møteinnslag = møter.map(møte => this.eksisterendeEllerOpprettInnslagFor(møte).oppdaterMøte(møte));
        const innslag = [...this.innslagAvTypene(InnslagType.ingenTidligere, InnslagType.stedfortrederFoer), ...this.sorterMøteinnslag(møteinnslag), ...this.innslagAvTypene(InnslagType.stedfortrederEtter, InnslagType.ingenSenere)];
        return this.kopi(innslag);
    }

    /** Returnerer kopi av innslag der angitt møte er oppdatert dersom det eksisterer i innslag fra før, ellers lagt til. */
    medMøte(møte: MøteMedAggregaterModell): InnslagSamling {
        if (this.inneholderInnslagForMøteId(møte.id))
            return this.medKunMøtene(this.møteinnslag().map(innslag => innslag.moeteinfo()!.id == møte.id ? møte : innslag.moeteinfo()!));
        else
            return this.medKunMøtene([...this.møteinnslag().map(innslag => innslag.moeteinfo()!), møte]);
    }

    /** Returnerer kopi med de angitte møter lagt til som innslag på starten av eksisterende møter og antall stedfortredere på starten tilpasset tilgjengelig horisontal bredde */
    medTidligereMøter(møter: MøteMedAggregaterModell[], ingenTidligereMøter: boolean) {
        debug(`medTidligereMøter: mottatt ${møter.length} møter`, møter)
        //sørg for tilstrekkelig antall stedfortedere som kan populeres med møter
        let kopi = this.medAntallStedfortredereFør(møter.length);
        return kopi
          //populer stedfortredere med møtedata
          .oppdaterInnslagMedMøteinfo(møter.reverse(), kopi.innslagAvTypene(InnslagType.stedfortrederFoer).reverse())
          //legg til nye stedfortredere, med mindre det ikke finnes tidligere møter
          .medStedfortredereFør(ingenTidligereMøter);
    }

    /** Returnerer kopi med de angitte møter lagt til som innslag på slutten av eksisterende møter og antall stedfortredere på slutten tilpasset tilgjengelig horisontal bredde */
    medSenereMøter(møter: MøteMedAggregaterModell[], ingenSenereMøter: boolean) {
        debug(`medSenereMøter: mottatt ${møter.length} møter`, møter)
        //sørg for tilstrekkelig antall stedfortedere som kan populeres med møter
        let kopi = this.medAntallStedfortredereEtter(møter.length);
        return kopi
          //populer stedfortredere med møtedata
          .oppdaterInnslagMedMøteinfo(møter, kopi.innslagAvTypene(InnslagType.stedfortrederEtter))
          //legg til nye stedfortredere, med mindre det ikke finnes tidligere møter
          .medStedfortredereEtter(ingenSenereMøter);
    }

    /** Returnerer kopi med de samme innslagene fra og med 'møter', men antallet stedfortredere på starten tilpasset tilgjengelig horisontal bredde */
    medStedfortredereFør(ingenTidligereMøter: boolean): InnslagSamling {
        const stedfortredere = ingenTidligereMøter ? [Innslag.stedfortreder(InnslagType.ingenTidligere)] : Innslag.stedfortredere(this.avhengigheter.beregnAntallInnslag(), InnslagType.stedfortrederFoer);
        const innslag = [...stedfortredere, ...this.innslagAvTypene(InnslagType.moete, InnslagType.stedfortrederEtter, InnslagType.ingenSenere)];
        return this.kopi(innslag);
    }

    /** Returnerer kopi med de samme innslagene fra og med 'møter', men med angitt antallet stedfortredere på starten */
    medAntallStedfortredereFør(antall: number): InnslagSamling {
        return this.kopi([...(Innslag.stedfortredere(antall, InnslagType.stedfortrederFoer)), ...this.innslagAvTypene(InnslagType.moete, InnslagType.stedfortrederEtter, InnslagType.ingenSenere)]);
    }

    /** Returnerer kopi med de samme innslagene til og med 'møter', men antallet stedfortredere på slutten tilpasset tilgjengelig horisontal bredde */
    medStedfortredereEtter(ingenSenereMøter: boolean): InnslagSamling {
        const stedfortredere = ingenSenereMøter ? [Innslag.stedfortreder(InnslagType.ingenSenere)] : Innslag.stedfortredere(this.avhengigheter.beregnAntallInnslag(), InnslagType.stedfortrederEtter);
        const innslag = [...this.innslagAvTypene(InnslagType.moete, InnslagType.stedfortrederFoer, InnslagType.ingenTidligere), ...stedfortredere,];
        return this.kopi(innslag);
    }

    /** Returnerer kopi med de samme innslagene til og med 'møter', men med angitt antallet stedfortredere på slutten */
    medAntallStedfortredereEtter(antall: number): InnslagSamling {
        return this.kopi([...this.innslagAvTypene(InnslagType.moete, InnslagType.stedfortrederFoer, InnslagType.ingenTidligere), ...(Innslag.stedfortredere(antall, InnslagType.stedfortrederEtter))]);
    }

    /** Returnerer kopi med de samme møteinnslagene, men antallet stedfortredere er tilpasset tilgjengelig horisontal bredde */
    medReberegnetUtsnitt(): InnslagSamling {
        return this.kopi(this.møteinnslag())
          .medStedfortredereFør(this.inneholderInnslagAvType(InnslagType.ingenTidligere))
          .medStedfortredereEtter(this.inneholderInnslagAvType(InnslagType.ingenSenere));
    }

    /** Returnerer periode som spenner fra først møtets startdato til siste møtets startdato.
     * Dersom ingen møter tilgjengelig returneres periode som spenner over 'nå' + en dag (start < slutt).
     * Dersom vi har nådd ytterkanten av tilgjengelige møter (ingenTidligere/ingenSenere) utvider vi med ett år for
     * slik at nye møter informert om via endringshendelse med stor sannsynlighet dukker opp i visningen.*/
    gjeldendePeriode(): ZonedDateTimePeriodeLukket {
        const alleMøter = this.møteinnslag();
        if (erTomListe(alleMøter)) return new ZonedDateTimePeriodeLukket(this.avhengigheter.klokke.nåSomZonedDateTime(), this.avhengigheter.klokke.nåSomZonedDateTime().add({days:1}));
        const start = lesMøtedato2(første(alleMøter)!.moeteinfo()!.moete).start;
        const slutt = lesMøtedato2(siste(alleMøter)!.moeteinfo()!.moete).start;
        return new ZonedDateTimePeriodeLukket(
          this.inneholderInnslagAvType(InnslagType.ingenTidligere) ? start.subtract({years: 1}) : start,
          this.inneholderInnslagAvType(InnslagType.ingenSenere) ? slutt.add({years: 1}) : slutt);
    }

    på(posisjon: number): Innslag {
        return this.innslag[posisjon];
    }

    førsteIkkeAvholdteMøteinnslag(): Innslag | null {
        return this.møteinnslag().find(innslag => !erMøteAvholdt(innslag.moeteinfo()!.moete, this.avhengigheter.klokke)) ?? null;
    }

    sisteMøteinnslag(): Innslag | null {
        return siste(this.møteinnslag());
    }

    førsteInnslag(): Innslag | null {
        return første(this.innslag);
    }

    sisteInnslag(): Innslag | null {
        return siste(this.innslag);
    }

    innslagForMøteId(møteId: MøteId): Innslag | null {
        return this.møteinnslag().find(innslag => innslag.moeteinfo()!.moete.id == møteId) ?? null;
    }

    møteIder(): MøteId[] {
        return this.møter().map(møte => møte.id);
    }

    inneholderInnslagAvType(type: InnslagType): boolean {
        return this.innslag.some(innslag => innslag.type() == type);
    }

    inneholderInnslagForMøteId(møteId: MøteId): boolean {
        return erIkkeNil(this.innslagForMøteId(møteId));
    }

    inneholderInnslagKnyttetTilPlan(planId: PlanId) {
        return this.møter().some(møte => møte.planId == planId);
    }

    inneholderIngenMøter() {
        return erTomListe(this.møteinnslag());
    }

    toString() {
        return `innslag:\n${this.innslag.map(innslag => innslag.toString()).join(',\n')}`;
    }

    //private hjelpemetoder

    private kopi(innslag: Innslag[]): InnslagSamling {
        const kopi = new InnslagSamling(this.avhengigheter, innslag);
        return kopi;
    }

    private oppdaterInnslagMedMøteinfo(møter: MøteMedAggregaterModell[], innslag: Innslag[]): InnslagSamling {
        møter.forEach((møte, indeks) => {
            debug(`oppdaterInnslagMedMøteinfo: init innslag ${innslag[indeks].toString()} med møte ${møte?.moete.id}`, møte);
            innslag[indeks].initMøteinfo(this.somMøteinfo(møte));
        });
        return this;
    }

    private eksisterendeEllerOpprettInnslagFor(møte: MøteMedAggregaterModell): Innslag {
        return this.inneholderInnslagForMøteId(møte.id) ? this.innslagForMøteId(møte.id)! : Innslag.møte(this.somMøteinfo(møte))
    }

    private somMøteinfo(møte: MøteMedAggregaterModell): Møteinfo {
        return {møte: møte, gruppe: this.avhengigheter.gruppe(møte.moete.eiendeGruppeId)};
    }

    private innslagAvTypene(...typer: InnslagType[]): Innslag[] {
        return this.innslag.filter(innslag => typer.includes(innslag.type()));
    }

    private møteinnslag(): Innslag[] {
        return this.innslagAvTypene(InnslagType.moete);
    }

    private møter(): MøteModellEnkel[] {
        return this.møteinnslag().map(innslag => innslag.moeteinfo()!.moete);
    }

    private sorterMøteinnslag(møteinnslag: Innslag[]): Innslag[] {
        return møteinnslag.sort((a, b) => sorterMøterPåDato(a.moeteinfo()!.moete, b.moeteinfo()!.moete));
    }
}

interface InnslagSamlingAvhengigheter {
    klokke: Klokke;
    gruppe: (id: GruppeId) => EnkelGruppeModell;
    beregnAntallInnslag: () => number;
}


@Injectable()
export class HorisontalMøtenavigasjonKomponentAvhengigheter {

    constructor(private møteMedAggregaterDatatjeneste: MøteMedAggregaterDatatjeneste, private møteRessurs: MøteRessursClient) {
    }

    førstkommendeMøteOgOmkringliggende(maksAntall: number): Observable<OmkringliggendeMøterModell> {
        return this.møteMedAggregaterDatatjeneste.førstkommendeMøteOgOmkringliggende(maksAntall);
    }

    møteOgOmkringliggende(møteId: MøteId, maksAntall: number): Observable<OmkringliggendeMøterModell> {
        return this.møteMedAggregaterDatatjeneste.møteOgOmkringliggende(møteId, maksAntall);
    }

    finnTidligereMøter(før: ZonedDateTime, maksAntall: number): Observable<MøteMedAggregaterModell[]> {
        return this.møteMedAggregaterDatatjeneste.finnTidligereMøter(før, maksAntall);
    }

    finnSenereMøter(etter: ZonedDateTime, maksAntall: number): Observable<MøteMedAggregaterModell[]> {
        return this.møteMedAggregaterDatatjeneste.finnSenereMøter(etter, maksAntall);
    }

    møterIPeriode(periode: ZonedDateTimePeriodeLukket): Observable<MøteMedAggregaterModell[]> {
        return this.møteMedAggregaterDatatjeneste.finnMøterIPerioden(periode);
    }

    møteendringer(møteIder: MøteId[]): Observable<MøteMedAggregaterModell[]> {
        return this.møteMedAggregaterDatatjeneste.hentMangeFraFraLokallagerSomObservable(møteIder)
    }

    hentMøte(id: MøteId): Observable<MøteMedAggregaterModell> {
        return this.møteMedAggregaterDatatjeneste.hent(id);
    }

    async møtedato(møteId: MøteId): Promise<ZonedDateTimePeriodeLukket> {
        const møte = await this.møteRessurs.hentEnkel(møteId).somPromise();
        return lesMøtedato2(møte);
    }

}
